import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import WaveformViewer from './WaveformViewer';
import QueuePanel from './QueuePanel';
import './PersistentPlayer.css';

const PersistentPlayer: React.FC = () => {
  const {
    currentStream,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    currentQuality,
    queue,
    togglePlayPause,
    seek,
    setVolume,
    toggleMute,
    clearStream,
    playNext,
    playPrevious,
    hasNext,
    hasPrevious,
  } = useAudioPlayer();

  const [showQueue, setShowQueue] = useState(false);

  const navigate = useNavigate();

  if (!currentStream) {
    return null;
  }

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(parseFloat(e.target.value));
  };

  const handleStreamClick = () => {
    navigate(`/streams/${currentStream.streamId}`);
  };

  return (
    <div className="persistent-player">
      <div className="player-waveform-container">
        <WaveformViewer
          audioUrl={currentStream.audioUrl}
          currentTime={currentTime}
          duration={duration}
          onSeek={seek}
          height={48}
          waveformData={currentStream.waveformData}
        />
      </div>

      <div className="player-content">
        {/* Stream Info */}
        <div className="player-info" onClick={handleStreamClick}>
          {currentStream.coverImage ? (
            <img src={currentStream.coverImage} alt={currentStream.name} className="player-cover" />
          ) : (
            <div className="player-cover player-cover-placeholder">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
              </svg>
            </div>
          )}
          <div className="player-details">
            <div className="player-name">
              <span className="player-name-text">{currentStream.name}</span>
              {currentQuality === 'lossless' && (
                <span className="player-quality-badge" title="FLAC lossless — native resolution">
                  Lossless
                </span>
              )}
            </div>
            {currentStream.uploaderDisplayName && (
              <div className="player-uploader">{currentStream.uploaderDisplayName}</div>
            )}
          </div>
        </div>

        {/* Playback Controls */}
        <div className="player-controls">
          <button
            className={`player-btn player-btn-skip ${!hasPrevious ? 'disabled' : ''}`}
            onClick={playPrevious}
            disabled={!hasPrevious}
            title="Previous"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
            </svg>
          </button>

          <button className="player-btn player-btn-play" onClick={togglePlayPause}>
            {isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>

          <button
            className={`player-btn player-btn-skip ${!hasNext ? 'disabled' : ''}`}
            onClick={playNext}
            disabled={!hasNext}
            title="Next"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
            </svg>
          </button>

          <div className="player-time">
            <span>{formatTime(currentTime)}</span>
            <span className="player-time-separator">/</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Volume & Close */}
        <div className="player-secondary">
          <div className="player-volume">
            <button className="player-btn player-btn-volume" onClick={toggleMute}>
              {isMuted || volume === 0 ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                </svg>
              ) : volume < 0.5 ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 9v6h4l5 5V4l-5 5H7z"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                </svg>
              )}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className="player-volume-slider"
            />
          </div>

          <button
            className={`player-btn player-btn-queue ${showQueue ? 'active' : ''}`}
            onClick={() => setShowQueue(!showQueue)}
            title={`Queue (${queue.length})`}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>
            </svg>
            {queue.length > 1 && (
              <span className="queue-badge">{queue.length}</span>
            )}
          </button>

          <button className="player-btn player-btn-close" onClick={clearStream} title="Close player">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Queue Panel */}
      <QueuePanel isOpen={showQueue} onClose={() => setShowQueue(false)} />
    </div>
  );
};

export default PersistentPlayer;
